import { 
  Box, 
  Button, 
  Typography, 
  Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useAnalytics } from "globals/hooks";
import { AppInfoContent } from '../../AppInfoSections';
import EmptyList from "components/globals/EmptyList";

import { black, successGreen, greenLight, moovsBlueSelected } from 'design-system/colors';

import { MoovsMarketAppData } from 'pages/moovs-market/components/MoovsMarketAppList/hooks/useMoovsMarketApps';

type PassengerAppInstalledProps = {
  app: MoovsMarketAppData;
  isMobileView: boolean;
  companyName: string;
};

export function PassengerAppInstalled(props: PassengerAppInstalledProps) {
  const { app, isMobileView, companyName } = props;
  
  // Hooks
  const { track } = useAnalytics();

  // Derived state
  const liveSubscription = app.liveSubscription;

  // Handlers
  const handleViewKnowledgeBaseClick = () => {
    track('market_passengerAppViewKnowledgeBaseButtonClicked');
    window.open("http://support.moovsapp.com/en/collections/6388357-moovs-passenger-app", "_blank");
  };

  const handleScheduleCallClick = () => {
    track('market_passengerAppScheduleCallClicked');
    window.open("https://calendly.com/joan-moovsapp/passenger-app-demo", "_blank");
  };

  return liveSubscription ? (
    <Box>
      {/* Moovs Market App Headers */}
      <AppInfoContent
        app={app}
        isMobileView={isMobileView}
        companyName={companyName}
      />

      {/* Success message */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: greenLight,
          borderRadius: '4px',
          p: 3,
          mb: 5,
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        <CheckCircleIcon sx={{ color: successGreen, mr: 2, mt: 0.5 }} />
        <Box>
          <Typography variant="h4" color={black} gutterBottom>
            You've successfully subscribed to the Passenger App!
          </Typography>
          <Typography variant="body1" color={black}>
            We're excited to help you launch your branded passenger experience.
          </Typography>
        </Box>
      </Paper>

      {/* View knowledge base button */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ py: 1.5, mb: 9, borderRadius: '4px' }}
        onClick={handleViewKnowledgeBaseClick}
      >
        View knowledge base
      </Button>

      {/* App specialist section */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: moovsBlueSelected,
          borderRadius: '4px',
          p: 3,
        }}
      >
        <Typography variant="h3" gutterBottom pb={1}>
          Your app specialist
        </Typography>
        <Typography variant="body1" color={black} mb={2}>
          Have questions or need assistance? Schedule time with your dedicated app specialist.
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          sx={{ borderRadius: '4px' }}
          onClick={handleScheduleCallClick}
        >
          Schedule a call
        </Button>
      </Paper>
    </Box>
  ) : (
    <Box
      height="calc(100vh - 190px)"
      display="flex"
      justifyContent="center"
      alignItems="center"
      mr={1}
      ml={1}
    >
      <EmptyList
        title="You've successfully subscribed to the Passenger App!"
        copy="We're excited to help you launch your branded passenger experience."
        imageUrl={app.icon}
        ctaCopy="Ready to launch? Book a call with our dedicated app specialist"
        secondaryCtaCopy="View knowledge base"
        onCreateClick={handleScheduleCallClick}
        onSecondaryClick={handleViewKnowledgeBaseClick}
      />
    </Box>
  );
} 